import * as React from 'react';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { getCreatedAt, getRequestRole, getRequestStatus } from 'Src/helpers/accessor/accessor';
import { Request } from 'Src/models/request';
import { getSelectOptionsForRequestArchiveStatus } from 'Src/helpers/table/select';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import propz from 'propz';
import {
  getAllArchiveRequests,
  getArchiveRequests,
  getArchiveRequestsCount
} from 'Src/helpers/service/admin/requestsArchive';
import '../Students/Students.scss';
import { PeopleColumnsMenu } from '../PeopleColumnsMenu';
import { countActivePeopleFilters, PeopleFilterChips } from '../PeopleFilterChips';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import moment from 'moment';
import { PeopleFilterChipSelect } from '../PeopleFilterChipSelect';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name or surname',
    field: 'nameSearch',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'firstName']
  },
  {
    text: 'Date',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getCreatedAt
  },
  {
    text: 'Name',
    field: 'requester.firstName',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['requester', 'firstName']
  },
  {
    text: 'Surname',
    field: 'requester.lastName',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: ['requester', 'lastName']
  },
  {
    text: 'Role',
    field: 'requestedPermissionPreset',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getRequestRole
  },
  {
    text: 'Email',
    field: 'requester.email',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'email']
  },
  {
    text: 'Phone',
    field: 'requester.phone',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['requester', 'phone']
  },
  {
    text: 'Comment',
    field: 'comment',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['requestedPermission', 'comment']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getRequestStatus
  }
];

const getRequestsWhere = (filters: any): any => {
  const { nameSearch, ...fieldFilters } = filters || {};
  const where = getServerFieldSectionWhere(fieldFilters);
  const term = String(nameSearch || '').trim();
  if (term) {
    where.nameSearch = { like: term, options: 'i' };
  }
  if (filters['requester.email']) {
    where['requester.email'] = { like: filters['requester.email'], options: 'i' };
  }
  if (filters['requester.phone']) {
    where['requester.phone'] = { like: filters['requester.phone'], options: 'i' };
  }
  const createdAt = filters.createdAt || {};
  if (createdAt.from || createdAt.to) {
    const range: any = {};
    if (createdAt.from) {
      range.$gte = moment(createdAt.from, ['DD-MM-YYYY', 'YYYY-MM-DD'], true)
        .startOf('day')
        .toISOString();
    }
    if (createdAt.to) {
      range.$lte = moment(createdAt.to, ['DD-MM-YYYY', 'YYYY-MM-DD'], true)
        .endOf('day')
        .toISOString();
    }
    where.createdAt = range;
  }
  return where;
};

const getRequestDateRange = (filters: any): any => {
  const createdAt = filters.createdAt || {};
  const parseDate = (value: string): Date | null => {
    const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.toDate() : null;
  };
  return { start: parseDate(createdAt.from || ''), end: parseDate(createdAt.to || '') };
};

interface State {
  items: Request[];
  currentPage: number;
  selectedItems: Request[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  previewItem?: Request;
  basePath: string;
  hiddenColumns: string[];
  requestDateRange: any;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  onTotalChange?: (total: number) => void;
}

export class RequestsArchive extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      previewItem: undefined,
      basePath: '',
      hiddenColumns: loadHiddenColumns(props.user, 'people.roleRequests.archive'),
      requestDateRange: { start: null, end: null }
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
    document.addEventListener('mousedown', this.onFilterOutsideClick);
    document.addEventListener('keydown', this.onFilterKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onFilterOutsideClick);
    document.removeEventListener('keydown', this.onFilterKeyDown);
  }

  onFilterOutsideClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.requests-filter') && !target.closest('.people-filterBtn')) {
      this.setState({ isShowFilter: false });
      if (this.state.isShowFilter) this.setItems();
    }
  };
  onFilterKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.setState({ isShowFilter: false });
      if (this.state.isShowFilter) this.setItems();
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const { user, location } = props;

    const search = parse(location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getRequestsWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getItemsPromise = getArchiveRequests(user, serverQueryFilter);
    const getItemsCountPromise = getArchiveRequestsCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return BPromise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        requestDateRange: getRequestDateRange(filters),
        items: items,
        total: countObj.count,
        basePath: location.pathname,
        isLoading: false
      });
      if (typeof props.onTotalChange === 'function') {
        props.onTotalChange(countObj.count);
      }

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getRequestsWhere(filters);

    getAllArchiveRequests(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      filters: {},
      requestDateRange: { start: null, end: null },
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  getRequesterName(request: Request): string {
    const firstName = propz.get(request, ['requester', 'firstName'], '');
    const lastName = propz.get(request, ['requester', 'lastName'], '');
    return `${firstName} ${lastName}`.trim() || 'Unknown requester';
  }

  getRequesterInitials(request: Request): string {
    const firstName = propz.get(request, ['requester', 'firstName'], '?');
    const lastName = propz.get(request, ['requester', 'lastName'], '');
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }

  getRequestDate(request: Request): string {
    const createdAt = propz.get(request, ['createdAt'], '');
    if (!createdAt) {
      return '-';
    }

    return new Date(createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getRoleColor(role: string): string {
    switch (role) {
      case ROLE.ADMIN:
        return '#227ebd';
      case ROLE.MANAGER:
        return '#7b5bd6';
      case ROLE.TEACHER:
        return '#2f9e5a';
      case ROLE.COACH:
        return '#f08c1a';
      case ROLE.PARENT:
        return '#0f8a8a';
      case ROLE.STUDENT:
        return '#d64550';
      default:
        return '#64748b';
    }
  }

  renderRoleChip(role: string): React.ReactNode {
    const color = this.getRoleColor(role);
    return (
      <span className="requests-roleChip" style={{ '--requests-role-color': color } as React.CSSProperties}>
        <i />
        <span>{ROLE_SERVER_TO_CLIENT_MAPPING[role] || role}</span>
      </span>
    );
  }

  renderStatusBadge(status: string): React.ReactNode {
    const normalizedStatus = status || 'ARCHIVE';
    const label = normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1).toLowerCase();
    const className =
      normalizedStatus === 'ACCEPTED'
        ? 'requests-statusBadge requests-statusBadge--accepted'
        : normalizedStatus === 'REJECTED'
        ? 'requests-statusBadge requests-statusBadge--rejected'
        : 'requests-statusBadge';

    return <span className={className}>{label}</span>;
  }

  setSimpleFilter = (filterField: string, value: string): void => {
    const isMulti = filterField === 'requestedPermissionPreset' || filterField === 'status';
    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: isMulti ? (value ? [value] : []) : value
      }
    });
  };

  toggleMultiFilter = (filterField: string, value: string): void => {
    const current = Array.isArray(this.state.filters[filterField]) ? this.state.filters[filterField] : [];
    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: current.includes(value) ? current.filter(item => item !== value) : [...current, value]
      }
    });
  };

  setSearchFilter = (filterField: string, value: string): void => {
    this.setState({ filters: { ...this.state.filters, [filterField]: value } });
  };

  setDateRangeFilter = (value: any): void => {
    const requestDateRange = value || { start: null, end: null };
    this.setState(
      {
        requestDateRange,
        filters: {
          ...this.state.filters,
          createdAt: {
            from: requestDateRange.start ? moment(requestDateRange.start).format('DD-MM-YYYY') : '',
            to: requestDateRange.end ? moment(requestDateRange.end).format('DD-MM-YYYY') : ''
          }
        }
      },
      this.onApplyFilterClick
    );
  };

  setDateFilter = (edge: string, value: string): void => {
    const currentFilterField = this.state.filters.createdAt || {};
    this.setState({
      filters: {
        ...this.state.filters,
        createdAt: {
          ...currentFilterField,
          [edge]: value
        }
      }
    });
  };

  getFilterValue(filterField: string): string {
    const value = this.state.filters[filterField];
    return Array.isArray(value) ? propz.get(value, [0], '') : value || '';
  }

  renderPagination(): React.ReactNode {
    const { currentPage, total } = this.state;
    return total > 0 ? (
      <Pagination2 modern currentPage={currentPage} total={total} setCurrentPageParams={this.setCurrentPageParams} />
    ) : null;
  }

  renderFilters(filterOptions): React.ReactNode {
    return (
      <form
        className="people-filter requests-filter"
        onSubmit={event => {
          event.preventDefault();
          this.onApplyFilterClick();
        }}
      >
        <div className="people-filter__header">
          <strong>Filters</strong>
          <button
            type="button"
            onClick={() => {
              this.setState({ isShowFilter: false });
              this.setItems();
            }}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>
        <div className="people-filter__body">
          <label className="people-filter__field">
            <span>Email</span>
            <input
              value={this.getFilterValue('requester.email')}
              placeholder="Search by email"
              onChange={event => this.setSimpleFilter('requester.email', event.target.value)}
            />
          </label>
          <label className="people-filter__field">
            <span>Phone</span>
            <input
              value={this.getFilterValue('requester.phone')}
              placeholder="Search by phone"
              onChange={event => this.setSimpleFilter('requester.phone', event.target.value)}
            />
          </label>
          <PeopleFilterChipSelect
            label="Status"
            selected={(this.state.filters.status || []).map(String)}
            options={filterOptions.status.map(option => ({ value: String(option.value), label: String(option.text) }))}
            onToggle={value => this.toggleMultiFilter('status', value)}
          />
          <div className="requests-dateRange">
            <SitDatePicker
              label="Request date"
              mode="range"
              value={this.state.requestDateRange}
              onChange={this.setDateRangeFilter}
              format="DD-MM-YYYY"
              placeholder="Select date range"
              numberOfMonths={2}
              align="left"
              clearable={true}
            />
          </div>
        </div>
        <div className="people-filter__actions">
          <button type="button" className="people-btn people-btn--ghost" onClick={this.onClearFilterClick}>
            Clear all
          </button>
          <div>
            <button
              type="button"
              className="people-btn people-btn--secondary"
              onClick={() => {
                this.setState({ isShowFilter: false });
                this.setItems();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="people-btn people-btn--primary">
              Apply
            </button>
          </div>
        </div>
      </form>
    );
  }

  openRequestPreview = (request: Request): void => {
    this.setState({ previewItem: request });
  };

  closeRequestPreview = (): void => {
    this.setState({ previewItem: undefined });
  };

  renderDetailField(label: string, value: React.ReactNode): React.ReactNode {
    return (
      <div className="requests-drawerField">
        <span>{label}</span>
        <strong>{value || '-'}</strong>
      </div>
    );
  }

  renderArchiveDrawer(): React.ReactNode {
    const { previewItem } = this.state;

    if (!previewItem) {
      return null;
    }

    const role = propz.get(previewItem, ['requestedPermission', 'preset'], '');
    const comment = propz.get(previewItem, ['requestedPermission', 'comment'], '');
    const email = propz.get(previewItem, ['requester', 'email'], '');
    const phone = propz.get(previewItem, ['requester', 'phone'], '');
    const status = propz.get(previewItem, ['status'], '');

    return (
      <>
        <div className="requests-drawerScrim" onClick={this.closeRequestPreview} />
        <aside className="requests-drawer" role="dialog" aria-modal="true">
          <div className="requests-drawer__head">
            <div className="requests-drawer__identity">
              <span className="requests-avatar requests-avatar--lg">
                <span>{this.getRequesterInitials(previewItem)}</span>
              </span>
              <div>
                <h2>{this.getRequesterName(previewItem)}</h2>
                <p>
                  {ROLE_SERVER_TO_CLIENT_MAPPING[role] || role} request · {this.getRequestDate(previewItem)}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="requests-drawer__close"
              onClick={this.closeRequestPreview}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="requests-drawer__body">
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Status</div>
              {this.renderStatusBadge(status)}
            </section>
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Requested role</div>
              {this.renderRoleChip(role)}
            </section>
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Contact</div>
              {this.renderDetailField('Email', email)}
              {this.renderDetailField('Phone', phone)}
            </section>
            <section className="requests-drawerSection">
              <div className="requests-drawerSection__title">Message</div>
              <div className="requests-drawerMessage">{comment || 'No message left with this request.'}</div>
            </section>
          </div>
          <div className="requests-drawer__foot">
            <button type="button" className="people-btn people-btn--secondary" onClick={this.closeRequestPreview}>
              Close
            </button>
          </div>
        </aside>
      </>
    );
  }
  renderArchiveTable(): React.ReactNode {
    const { items } = this.state;

    if (items.length === 0) {
      return (
        <div className="requests-empty">
          <div className="requests-empty__icon">A</div>
          <h3>Nothing in the archive</h3>
          <p>Accepted and rejected requests will be kept here for reference.</p>
        </div>
      );
    }

    return (
      <div className="people-tableCard requests-tableCard">
        <div className="people-tableScroll">
          <table
            className={`people-table requests-table requests-table--archive ${this.state.hiddenColumns
              .map(key => `requests-hide-${key}`)
              .join(' ')}`}
          >
            <thead>
              <tr>
                <th className="people-table__sortable" onClick={() => this.onTableSortClick('createdAt')}>
                  Date
                </th>
                <th className="people-table__sortable" onClick={() => this.onTableSortClick('requester.firstName')}>
                  Requester
                </th>
                <th>Role</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Comment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(request => {
                const email = propz.get(request, ['requester', 'email'], '');
                const phone = propz.get(request, ['requester', 'phone'], '');
                const role = propz.get(request, ['requestedPermission', 'preset'], '');
                const comment = propz.get(request, ['requestedPermission', 'comment'], '');
                const status = propz.get(request, ['status'], '');

                return (
                  <tr
                    key={request.id}
                    data-preview={propz.get(this.state.previewItem, ['id']) === request.id}
                    onClick={() => this.openRequestPreview(request)}
                  >
                    <td className="requests-dateCell">{this.getRequestDate(request)}</td>
                    <td>
                      <div className="requests-personCell">
                        <span className="requests-avatar">
                          <span>{this.getRequesterInitials(request)}</span>
                        </span>
                        <div>
                          <strong>{this.getRequesterName(request)}</strong>
                          <span>{email || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td>{this.renderRoleChip(role)}</td>
                    <td>{email || '-'}</td>
                    <td>{phone || '-'}</td>
                    <td>
                      <span className="requests-commentCell" title={comment}>
                        {comment || '-'}
                      </span>
                    </td>
                    <td>{this.renderStatusBadge(status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const { filters, isLoading, total } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const filterOptions = {
      status: getSelectOptionsForRequestArchiveStatus()
    };

    return (
      <div className="requests-modernScreen">
        <div className="people-toolbar requests-toolbar">
          <div className="requests-toolbar__row requests-toolbar__row--primary">
            <label className="unionAdminsSearch requests-nameSearch">
              <input
                value={this.getFilterValue('nameSearch')}
                placeholder="Search by name or surname…"
                onChange={event => this.setSimpleFilter('nameSearch', event.target.value)}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
            </label>
            <div className="requests-toolbar__controls">
              <button
                type="button"
                className={`people-btn people-btn--secondary people-btn--iconLeft people-filterBtn ${
                  this.state.isShowFilter ? 'people-btn--on' : ''
                }`}
                onClick={this.onTableFilterClick}
              >
                <span className="people-icon people-icon--filter" />
                Filters
                {countActivePeopleFilters(filters) > 0 && (
                  <span className="people-btn__badge">{countActivePeopleFilters(filters)}</span>
                )}
              </button>
              <PeopleColumnsMenu
                columns={[
                  { key: 'date', label: 'Date' },
                  { key: 'requester', label: 'Requester' },
                  { key: 'role', label: 'Role' },
                  { key: 'email', label: 'Email' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'comment', label: 'Comment' },
                  { key: 'status', label: 'Status' }
                ]}
                hidden={this.state.hiddenColumns}
                onChange={hiddenColumns => {
                  saveHiddenColumns(this.props.user, 'people.roleRequests.archive', hiddenColumns);
                  this.setState({ hiddenColumns });
                }}
              />
            </div>
          </div>
        </div>
        <div
          className={`requests-contentLayout ${this.state.isShowFilter ? 'requests-contentLayout--withFilter' : ''}`}
        >
          {this.state.isShowFilter ? (
            <>
              <div
                className="requests-filterScrim"
                onClick={() => {
                  this.setState({ isShowFilter: false });
                  this.setItems();
                }}
              />
              {this.renderFilters(filterOptions)}
            </>
          ) : null}
          <div className="requests-contentMain">
            <PeopleFilterChips
              filters={filters}
              columns={COLUMNS}
              valueLabels={{
                status: filterOptions.status.reduce((result, option) => {
                  result[String(option.value)] = option.text || String(option.value);
                  return result;
                }, {})
              }}
              onRemove={field => {
                const nextState: any = { filters: { ...filters, [field]: '' } };
                if (field === 'createdAt') nextState.requestDateRange = { start: null, end: null };
                this.setState(nextState, this.onApplyFilterClick);
              }}
              onClear={this.onClearFilterClick}
            />
            {this.renderArchiveTable()}
            {this.renderPagination()}
          </div>
        </div>
        {this.renderArchiveDrawer()}
      </div>
    );
  }
}
